import Dialog from "@mui/material/Dialog";
import { useEffect, useState } from "react";
import {
  PrimaryCTAButton,
  TertiaryCTAButton,
  ButtonSize,
  HStack,
  VStack,
} from "../../components/utils";
import { Label, TextArea } from "../../components/shared/InputField";
import { RadioGroup, RadioGroupItem } from "../../components/shared/RadioGroup";
import {
  BoomerangResolutionStrategy,
  BulkEmployeeImportRow,
} from "../../types/Employee";

const RESOLUTION_OPTIONS: Array<{
  value: BoomerangResolutionStrategy;
  title: string;
  description: string;
}> = [
  {
    value: "NOT_BOOMERANG",
    title: "Not a boomerang",
    description:
      "Create unrelated employees after correcting any conflicting identifiers.",
  },
  {
    value: "MERGE_EXISTING_EMPLOYEE",
    title: "Link and merge employee records",
    description:
      "Update the former employee records and retain prior details in activity history.",
  },
  {
    value: "CREATE_SEPARATE_EMPLOYEE",
    title: "Link and create separate employees",
    description: "Keep both employments linked and create new portal logins.",
  },
];

export type BulkResolutionChoice = {
  strategy: BoomerangResolutionStrategy;
  moveGrants: boolean;
  continueVesting: boolean;
  reason: string;
};

type BulkBoomerangResolutionDialogProps = {
  open: boolean;
  rows: BulkEmployeeImportRow[];
  onClose: () => void;
  onSave: (choice: BulkResolutionChoice) => void;
};

export function BulkBoomerangResolutionDialog({
  open,
  rows,
  onClose,
  onSave,
}: BulkBoomerangResolutionDialogProps) {
  const [strategy, setStrategy] =
    useState<BoomerangResolutionStrategy>("NOT_BOOMERANG");
  const [moveGrants, setMoveGrants] = useState(false);
  const [continueVesting, setContinueVesting] = useState(false);
  const [reason, setReason] = useState("");
  const hasAlreadyLinkedCandidate = rows.some((row) => {
    const candidateId =
      row.selectedCandidateId || row.resolution?.candidateEmployeeId;
    return row.candidates.some(
      (candidate) =>
        candidate.employeeId === candidateId && candidate.alreadyLinked
    );
  });

  useEffect(() => {
    if (!open) return;
    setStrategy("NOT_BOOMERANG");
    setMoveGrants(false);
    setContinueVesting(false);
    setReason("");
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <VStack className="max-h-[calc(100vh-32px)] bg-white">
        <VStack className="gap-1 border-b border-borderColor p-6">
          <h2 className="text-base font-semibold leading-6 text-headerColor">
            Resolve boomerang matches
          </h2>
          <p className="text-sm text-secondaryText">
            Apply one rejoin decision to {rows.length} selected employee record
            {rows.length === 1 ? "" : "s"}. Validate all rows before creating
            employees.
          </p>
        </VStack>
        <VStack className="gap-6 overflow-y-auto px-6 py-5">
          {hasAlreadyLinkedCandidate && (
            <HStack
              role="alert"
              className="items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3"
            >
              <p className="text-sm text-red-700">
                Link and create is unavailable because at least one selected
                former employment is already linked.
              </p>
            </HStack>
          )}
          <VStack className="gap-2">
            <Label>Choose how to create these employees</Label>
            <RadioGroup
              name="bulkEmployeeImportResolution"
              value={strategy}
              onValueChange={(next) => {
                setStrategy(next as BoomerangResolutionStrategy);
                setMoveGrants(false);
                setContinueVesting(false);
              }}
              className="gap-3"
            >
              {RESOLUTION_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-inputBorder p-4"
                >
                  <RadioGroupItem
                    value={option.value}
                    disabled={
                      option.value === "CREATE_SEPARATE_EMPLOYEE" &&
                      hasAlreadyLinkedCandidate
                    }
                  />
                  <VStack className="gap-1">
                    <span className="text-sm font-medium text-headerColor">
                      {option.title}
                    </span>
                    <span className="text-xs text-secondaryText">
                      {option.description}
                    </span>
                  </VStack>
                </label>
              ))}
            </RadioGroup>
          </VStack>
          {strategy === "MERGE_EXISTING_EMPLOYEE" && (
            <VStack className="gap-2">
              <Label>Should grant vesting continue?</Label>
              <RadioGroup
                name="bulkMergeVesting"
                value={continueVesting ? "yes" : "no"}
                onValueChange={(next) => setContinueVesting(next === "yes")}
                className="grid gap-3 sm:grid-cols-2"
              >
                {[
                  ["no", "No, keep the current grant state"],
                  ["yes", "Yes, restore eligible lapsed vesting"],
                ].map(([value, label]) => (
                  <label
                    key={value}
                    className="flex cursor-pointer items-start gap-3 rounded-lg border border-inputBorder p-4"
                  >
                    <RadioGroupItem value={value} />
                    <span className="text-sm text-headerColor">{label}</span>
                  </label>
                ))}
              </RadioGroup>
            </VStack>
          )}
          {strategy === "CREATE_SEPARATE_EMPLOYEE" && (
            <VStack className="gap-4">
              <VStack className="gap-2">
                <Label>Move former grants to the new employees?</Label>
                <RadioGroup
                  name="bulkMoveGrants"
                  value={moveGrants ? "yes" : "no"}
                  onValueChange={(next) => {
                    setMoveGrants(next === "yes");
                    if (next === "no") setContinueVesting(false);
                  }}
                  className="grid gap-3 sm:grid-cols-2"
                >
                  {[
                    ["no", "No, keep grants on the former employees"],
                    ["yes", "Yes, move all grants to the new employees"],
                  ].map(([value, label]) => (
                    <label
                      key={value}
                      className="flex cursor-pointer items-start gap-3 rounded-lg border border-inputBorder p-4"
                    >
                      <RadioGroupItem value={value} />
                      <span className="text-sm text-headerColor">{label}</span>
                    </label>
                  ))}
                </RadioGroup>
              </VStack>
              {moveGrants && (
                <VStack className="gap-2">
                  <Label>Should grant vesting continue?</Label>
                  <RadioGroup
                    name="bulkSeparateVesting"
                    value={continueVesting ? "yes" : "no"}
                    onValueChange={(next) => setContinueVesting(next === "yes")}
                    className="grid gap-3 sm:grid-cols-2"
                  >
                    {[
                      ["no", "No, preserve current vesting states"],
                      ["yes", "Yes, restore eligible lapsed vesting"],
                    ].map(([value, label]) => (
                      <label
                        key={value}
                        className="flex cursor-pointer items-start gap-3 rounded-lg border border-inputBorder p-4"
                      >
                        <RadioGroupItem value={value} />
                        <span className="text-sm text-headerColor">
                          {label}
                        </span>
                      </label>
                    ))}
                  </RadioGroup>
                </VStack>
              )}
            </VStack>
          )}
          <VStack className="gap-2">
            <Label htmlFor="bulk-employee-import-reason">
              Reason, optional
            </Label>
            <TextArea
              id="bulk-employee-import-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Applied to every selected employee record. Leave blank to keep existing notes."
            />
          </VStack>
        </VStack>
        <HStack className="w-full items-center justify-between gap-3 border-t border-borderColor px-6 py-4">
          <TertiaryCTAButton buttonSize={ButtonSize.MEDIUM} onClick={onClose}>
            Cancel
          </TertiaryCTAButton>
          <PrimaryCTAButton
            buttonSize={ButtonSize.MEDIUM}
            onClick={() =>
              onSave({ strategy, moveGrants, continueVesting, reason })
            }
          >
            Apply resolution
          </PrimaryCTAButton>
        </HStack>
      </VStack>
    </Dialog>
  );
}
